import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { _axios } from "../utils/axios";
import { TApplication } from "./levelThreeApplication.hooks";
import { TLevel1PaymentReceipt } from "./level-1-payment-approval.hooks";

export const accountantApplicationsKeyStore = {
  all: ["accountant-applications"] as const,
  lists: () => [...accountantApplicationsKeyStore.all, "list"] as const,
  list: (
    page: number,
    limit: number,
    sort: string,
    sortVal: number,
    search: string,
    statuses: string[]
  ) =>
    [
      ...accountantApplicationsKeyStore.lists(),
      { page, limit, sort, sortVal, search, statuses },
    ] as const,
  counts: (search: string, statuses: string[]) =>
    [
      ...accountantApplicationsKeyStore.all,
      "count",
      { search, statuses },
    ] as const,
  receipt: (applicationId: number | string) =>
    [...accountantApplicationsKeyStore.all, "receipt", applicationId] as const,
  applicantReceipts: (applicationId: number | string) =>
    [
      ...accountantApplicationsKeyStore.all,
      "applicant-receipts",
      applicationId,
    ] as const,
};

export function useAccountantApplicationCounts({
  search,
  statuses,
}: {
  search?: string;
  statuses?: string[];
}) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.counts(
      search || "",
      statuses || []
    ),
    queryFn: () =>
      _axios
        .post<{
          success: true;
          data: number;
        }>("/accountant-payment/count", { search, statuses })
        .then((res) => res.data),
    staleTime: 0,
    select: (d) => d.data,
  });
}

export function useAccountantApplications({
  page,
  limit,
  search,
  sort,
  sortVal,
  statuses,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  statuses?: string[];
}) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      statuses || []
    ),
    queryFn: () =>
      _axios
        .post<{
          success: true;
          hasMore?: boolean;
          data: (TApplication & {
            receipt_no?: string;
            amount_paid?: number | string;
            payment_date?: string;
            payment_status?: string;
            receipt_file?: string;
            totalAmount?: number | string;
          })[];
        }>("/accountant-payment/list", {
          sort,
          sortVal,
          search,
          page,
          limit,
          statuses: statuses || [],
        })
        .then((res) => res.data),
    staleTime: 0,
    select: (d) => d.data,
  });
}

export function useAccountantApproveReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { applicationId: number; comment?: string }) =>
      _axios.post("/accountant-payment/approve", payload).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountantApplicationsKeyStore.all,
      });
    },
  });
}

/**
 * The applicant's own bank slips for the Accountant's reconciliation screen.
 *
 * The Accountant used to read these through Level 1's receipts endpoint, but
 * that endpoint now returns the Accountant's official receipt, because Level
 * 1/2/3 no longer see the applicant slip. The rows keep the same shape as
 * before (including is_current), so the screen's active/superseded split is
 * unaffected.
 */
export function useAccountantApplicantReceipts({ id }: { id: number | string }) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.applicantReceipts(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TLevel1PaymentReceipt[];
      }>(`/accountant-payment/applicant-receipts/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 0,
  });
}

/**
 * The Accountant's own official receipt for an application: the live row, the
 * superseded rows kept as read-only history, and each level's recorded verdict.
 * This is the only receipt Level 1/2/3 ever see.
 */
export type TAccountantReceipt = {
  accountant_receipt_id: number;
  application_id: number;
  ds_doc_id?: number | null;
  file_url?: string | null;
  receipt_no?: string | null;
  amount_paid?: number | string | null;
  payment_date?: string | null;
  receipt_status: string;
  is_current: number;
  uploaded_by?: number | null;
  created_at?: string;
};

export type TAccountantReceiptAck = {
  ack_id: number;
  accountant_receipt_id: number;
  level: string;
  ack_status: string;
  reviewer_id?: number | null;
  created_at?: string;
  updated_at?: string | null;
};

export function useAccountantReceipt({
  applicationId,
  enabled = true,
}: {
  applicationId?: number | string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.receipt(applicationId || ""),
    queryFn: () =>
      _axios
        .get<{
          success: true;
          data: {
            current: TAccountantReceipt | null;
            history: TAccountantReceipt[];
            acknowledgments: TAccountantReceiptAck[];
          };
        }>("/accountant-payment/accountant-receipt", {
          params: { applicationId },
        })
        .then((res) => res.data),
    select: (d) => d.data,
    staleTime: 0,
    enabled: !!applicationId && enabled,
  });
}

/**
 * Uploads the official receipt and forwards the application to Level 1. Sent as
 * multipart because the file goes straight to blob storage server-side rather
 * than through a separate upload endpoint first.
 */
export function useSaveAccountantReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      applicationId: number;
      file: File;
      receiptNo?: string;
      amountPaid?: string | number;
      paymentDate?: string;
      comment?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("applicationId", String(payload.applicationId));
      if (payload.receiptNo) formData.append("receiptNo", payload.receiptNo);
      if (payload.amountPaid !== undefined && payload.amountPaid !== "")
        formData.append("amountPaid", String(payload.amountPaid));
      if (payload.paymentDate)
        formData.append("paymentDate", payload.paymentDate);
      if (payload.comment) formData.append("comment", payload.comment);

      return _axios
        .post("/accountant-payment/accountant-receipt", formData)
        .then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountantApplicationsKeyStore.all,
      });
    },
  });
}

export function useAccountantReturnReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { applicationId: number; comment?: string }) =>
      _axios.post("/accountant-payment/return", payload).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountantApplicationsKeyStore.all,
      });
    },
  });
}
